import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import {
  ReactFlow,
  Connection,
  EdgeChange,
  EdgeSelectionChange,
  NodeChange,
  NodePositionChange,
  NodeSelectionChange,
  OnConnectStartParams,
  PanOnScrollMode,
  ReactFlowInstance,
  useReactFlow,
} from 'reactflow';

import AddSourceStepButton from 'analysis-tool/ui/AddSourceStepButton';
import notifications from 'notifications';
import {ButtonStyle} from 'ui-library/buttons/Button';
import DefaultBackground from 'ui-library/reactflow/backgrounds/DefaultBackground';
import {
  getClientPointFromConnectEvent,
  isDropOnEmptyCanvas,
  projectClientPointToFlow,
} from 'ui-library/reactflow/connectionDropUtils';
import BaseControlButton from 'ui-library/reactflow/controls/BaseControlButton';
import HorizontalControls, {MAX_ZOOM, MIN_ZOOM} from 'ui-library/reactflow/controls/HorizontalControls';
import {useDefaultReactFlowProps} from 'ui-library/reactflow/ReactFlowConstants';
import * as keyboard from 'util/Keyboard';
import * as logging from 'util/Logging';
import {confirm} from 'util/Modals';
import removeNulls from 'util/removeNulls';

import {AnalysisFlowchartAction} from './actions/AnalysisFlowchartActionTypes';
import {
  AnalysisGraph,
  AnalysisGraphEdge,
  AnalysisGraphEdgeType,
  AnalysisGraphNodeType,
} from './analysis-graph/AnalysisGraphTypes';
import AnalysisGraphUtils from './analysis-graph/AnalysisGraphUtils';
import {useAnalysisFlowchartContext} from './AnalysisFlowchartContext';
import AnalysisFlowchartEdge from './AnalysisFlowchartEdge';
import AnalysisFlowchartNode from './AnalysisFlowchartNode';
import AnalysisFlowchartToolbar from './menus/AnalysisFlowchartToolbar';
import nodePositionsGlobalState from './nodePositionsGlobalState';

const STATIC_PROPS: Partial<Parameters<typeof ReactFlow>[0]> = {
  connectOnClick: false,
  fitView: true,
  fitViewOptions: {padding: 0.2, minZoom: MIN_ZOOM, maxZoom: MAX_ZOOM},
  maxZoom: MAX_ZOOM,
  minZoom: MIN_ZOOM,
  nodesFocusable: true,
  onInit: (reactFlowInstance: ReactFlowInstance) => {
    reactFlowInstance.fitView();
  },
  panOnScroll: true,
  panOnScrollMode: PanOnScrollMode.Free,
  panOnDrag: true,
  selectionKeyCode: 'Shift',
  selectNodesOnDrag: true,
  multiSelectionKeyCode: ['Control', 'Meta', 'Shift'],
};

function selectedStepsLabel(count: number): string {
  if (count === 0) {
    return 'selected steps';
  }
  return count === 1 ? '1 selected step' : `${count} selected steps`;
}

const EDGE_TYPES = {[AnalysisGraphEdgeType.EDGE]: AnalysisFlowchartEdge};
const NODE_TYPES = {[AnalysisGraphNodeType.STEP]: AnalysisFlowchartNode};

export type InternalAnalysisFlowchartProps = AnalysisGraph & {clientStateId: string};

/**
 * Internal helper component for `AnalysisFlowchart`, which assumes `ReactFlowProvider` and
 * `AnalysisFlowchartContext` contexts are available.
 *
 * @package
 */
export default function InternalAnalysisFlowchart({
  nodes,
  edges,
  clientStateId,
}: InternalAnalysisFlowchartProps): JSX.Element {
  const defaultReactFlowProps = useDefaultReactFlowProps();
  const {
    getActionInfo,
    highlightedStepIds,
    clearHighlights,
    selectedNodeIds,
    setSelectedNodeIds,
    selectedEdgeIds,
    setSelectedEdgeIds,
  } = useAnalysisFlowchartContext();
  const reactFlowInstance = useReactFlow();

  // When highlighted step IDs are provided, zoom and pan to fit only those nodes.
  useEffect(() => {
    if (highlightedStepIds.size > 0 && nodes.length > 0) {
      // Small timeout to allow the initial layout to settle before re-fitting.
      const timer = setTimeout(() => {
        reactFlowInstance.fitView({
          nodes: nodes.filter((node) => highlightedStepIds.has(node.data.step.id)),
          padding: 0.3,
          duration: 300,
          maxZoom: MAX_ZOOM,
        });
      }, 100);
      return () => {
        clearTimeout(timer);
      };
    }
    return undefined;
  }, [highlightedStepIds, nodes, reactFlowInstance]);

  // Handle Ctrl + C and Ctrl + V commands raised within the flowchart.
  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>): void => {
      if (keyboard.getNormalizedShortcut(event) === 'Ctrl + C' && selectedNodeIds.size > 0) {
        const {doAction} = getActionInfo(AnalysisFlowchartAction.COPY_TO_CLIPBOARD);
        doAction?.(...[...selectedNodeIds].map((nodeId) => AnalysisGraphUtils.getStepIdFromNodeId(nodeId)));
      } else if (keyboard.getNormalizedShortcut(event) === 'Ctrl + V') {
        const {doAction} = getActionInfo(AnalysisFlowchartAction.PASTE_FROM_CLIPBOARD);
        doAction?.();
      }
    },
    [getActionInfo, selectedNodeIds]
  );

  // Handle Ctrl + V commands raised from the `<body>` tag. This catches Ctrl + V commands raised when focus
  // is not in the flowchart, while excluding Ctrl + V events when the user is specifically editing a text
  // field outside the flowchart.
  useEffect(() => {
    const handlePaste = (event: KeyboardEvent): void => {
      if (
        keyboard.getNormalizedShortcut(event) === 'Ctrl + V' &&
        event?.target instanceof HTMLElement &&
        event.target.tagName === 'BODY'
      ) {
        const {doAction} = getActionInfo(AnalysisFlowchartAction.PASTE_FROM_CLIPBOARD);
        doAction?.();
      }
    };
    document.addEventListener('keydown', handlePaste);
    return () => {
      document.removeEventListener('keydown', handlePaste);
    };
  }, [getActionInfo]);

  const [nodePositions, setNodePositions] = nodePositionsGlobalState.useGlobalClientState(clientStateId);

  // We store separate copies of `nodes` and `edges` internally, to apply `ReactFlow` rendering properties.
  const updatedNodes = useMemo(
    () =>
      nodes.map((node) => ({
        ...node,
        connectable: getActionInfo(AnalysisFlowchartAction.CONNECT_STEPS, node.data.step.id).enabled,
        position: nodePositions[node.id] ?? node.position,
        selected: selectedNodeIds.has(node.id),
      })),
    [nodes, getActionInfo, nodePositions, selectedNodeIds]
  );
  const updatedEdges = useMemo(
    () =>
      edges.map((edge) => ({
        ...edge,
        selected: selectedEdgeIds.has(edge.id),
      })),
    [edges, selectedEdgeIds]
  );

  // Called by `ReactFlow` when a user takes an action to update a node, such as moving or selecting the node.
  const onNodesChange = useCallback(
    (changes: Array<NodeChange>) => {
      const positionChanges = changes
        .filter(({type}) => type === 'position')
        .filter(({position}: NodePositionChange) => position != null);
      const removedNodeIds = new Set(
        changes.flatMap((change) => (change.type === 'remove' ? [change.id] : []))
      );
      if (positionChanges.length > 0 || removedNodeIds.size > 0) {
        const newNodePositions = Object.fromEntries(
          Object.entries(nodePositions).filter(([nodeId]) => !removedNodeIds.has(nodeId))
        );
        positionChanges.forEach(({id, position}: NodePositionChange) => {
          newNodePositions[id] = position!;
        });
        setNodePositions(newNodePositions);
      }

      const selectionChanges = changes.filter(({type}) => type === 'select') as Array<NodeSelectionChange>;
      if (selectionChanges.length > 0) {
        const newSelectedNodeIds = new Set(selectedNodeIds);
        selectionChanges.forEach((change) => {
          if (change.selected) {
            newSelectedNodeIds.add(change.id);
          } else {
            newSelectedNodeIds.delete(change.id);
          }
        });
        setSelectedNodeIds(newSelectedNodeIds);
      }
    },
    [nodePositions, setNodePositions, selectedNodeIds, setSelectedNodeIds]
  );

  // Called by `ReactFlow` when a user takes an action to update a node, such as selecting the edge.
  const onEdgesChange = useCallback(
    (changes: Array<EdgeChange>) => {
      // Overwrite `selectedEdgeIds` entirely, each time the user selects an edge. This prevents users from
      // Ctrl+Clicking to select multiple edges, which isn't a useful action, since we do not support deleting
      // multiple edges at once. Users can still select multiple edges when highlighting nodes.
      const selectionChanges = changes.filter(({type}) => type === 'select') as Array<EdgeSelectionChange>;
      if (selectionChanges.length > 0) {
        setSelectedEdgeIds(new Set(selectionChanges.filter(({selected}) => selected).map(({id}) => id)));
      }
    },
    [setSelectedEdgeIds]
  );

  // Usually, the layout is refreshed whenever a node is added or removed. If this will be very disruptive,
  // remember nodes' current positions.
  const preventAutoLayout = useCallback(() => {
    setNodePositions(Object.fromEntries(updatedNodes.map((node) => [node.id, node.position])));
  }, [setNodePositions, updatedNodes]);

  // Callback for any new, removed, or updated connections. `edge` is the edge to change or remove (if any),
  // and `connection` is the new edge to create (if any).
  const onConnectionChange = useCallback(
    async (edge: AnalysisGraphEdge | null, connection: Connection | null): Promise<void> => {
      // Grab the IDs of the steps whose inputs will be modified
      const newToStepId = connection && nodes.find((node) => node.id === connection.target)?.data.step.id;
      const oldToStepId = edge && nodes.find((node) => node.id === edge.target)?.data.step.id;
      const {doAction, message} = getActionInfo(
        AnalysisFlowchartAction.CONNECT_STEPS,
        ...removeNulls([newToStepId, oldToStepId])
      );
      if (doAction == null) {
        notifications.info(`This connection cannot be updated. ${message ?? ''}`);
      }
      await doAction?.(edge && edge.data.edge, connection);
    },
    [getActionInfo, nodes]
  );

  // The output handle a drag started from, so a release on empty canvas can branch off it. `onConnect`
  // (a completed connection) clears it, so a snapped-to-handle drag doesn't also branch — ReactFlow 11
  // fires `onConnect` right before `onConnectEnd`. Reconnecting an edge fires `onConnectStart`/`onConnectEnd`
  // with the same params as a fresh drag, so `onEdgeUpdateStart` (which fires first) flags it to skip.
  const branchSource = useRef<{nodeId: string; handleId: string} | null>(null);
  const isReconnecting = useRef(false);

  // Called by `ReactFlow` when a new edge is drawn
  const onConnect = useCallback(
    (connection: Connection) => {
      branchSource.current = null;
      return onConnectionChange(null, connection);
    },
    [onConnectionChange]
  );

  // Called by `ReactFlow` when an edge is reconnected to a new source or target
  const onEdgeUpdate = useCallback(
    (edge: AnalysisGraphEdge, connection: Connection) => onConnectionChange(edge, connection),
    [onConnectionChange]
  );

  const onEdgeUpdateStart = useCallback(() => {
    isReconnecting.current = true;
  }, []);

  const onConnectStart = useCallback(
    (_event: unknown, {nodeId, handleId, handleType}: OnConnectStartParams) => {
      // Only a drag from an output handle branches, and never a reconnect (flagged by `onEdgeUpdateStart`).
      branchSource.current =
        !isReconnecting.current && handleType === 'source' && nodeId != null && handleId != null
          ? {nodeId, handleId}
          : null;
    },
    []
  );

  // Called by `ReactFlow` when an edge drag ends
  const onConnectEnd = useCallback(
    (event: MouseEvent | TouchEvent) => {
      const source = branchSource.current;
      branchSource.current = null;
      isReconnecting.current = false;

      if (source == null || !isDropOnEmptyCanvas(event)) {
        return;
      }

      const step = nodes.find((node) => node.id === source.nodeId)?.data.step;
      const stepData = step?.outputStepDatas.find(
        (outputStepData) => AnalysisGraphUtils.getHandleId(outputStepData) === source.handleId
      );
      if (step == null || stepData == null) {
        return;
      }

      const point = getClientPointFromConnectEvent(event);
      const target = event.target as Element;
      if (point == null) {
        return;
      }
      const pane = target.closest('.react-flow__pane') ?? target;
      const position = projectClientPointToFlow(
        reactFlowInstance.project,
        point.clientX,
        point.clientY,
        pane
      );

      const {doAction} = getActionInfo(AnalysisFlowchartAction.OPEN_ADD_STEP, step.id);
      doAction?.(step.id, stepData.name, true, position);
    },
    [nodes, getActionInfo, reactFlowInstance]
  );

  // ReactFlow calls `onNodesDelete` if the user clicks backspace when a node is selected.
  const onNodesDelete = useCallback(
    (deletedNodes: Array<{id: string}>) => {
      const stepIds = deletedNodes.map((node) => AnalysisGraphUtils.getStepIdFromNodeId(node.id));
      const {doAction} = getActionInfo(AnalysisFlowchartAction.DELETE_STEPS, ...stepIds);
      if (doAction == null) {
        return;
      }
      confirm(
        `Delete steps`,
        `Are you sure you want to delete ${selectedStepsLabel(stepIds.length)}?`,
        ButtonStyle.NEGATIVE,
        {
          enableRedesign: true,
          onSubmit: () => doAction(...stepIds),
        }
      );
    },
    [getActionInfo]
  );

  // ReactFlow calls `onEdgesDelete` if the user clicks backspace when an edge (but no nodes) is selected.
  const onEdgesDelete = useCallback(
    (edges: Array<AnalysisGraphEdge>) => {
      // The flowchart UI doesn't currently support deleting multiple edges at once.
      if (edges.length > 1) {
        logging.info('Tried to delete more than 1 edge');
      }
      if (!getActionInfo(AnalysisFlowchartAction.CONNECT_STEPS, edges[0].data.edge.toStep.id).enabled) {
        return;
      }
      confirm(
        'Delete edge',
        'Deleting this edge will remove the relationship between the steps, possibly causing following steps to fail.',
        ButtonStyle.NEGATIVE,
        {
          enableRedesign: true,
          onSubmit: () => {
            preventAutoLayout(); // Do not auto-layout nodes after an edge is deleted
            onConnectionChange(edges[0], null);
            setSelectedEdgeIds(new Set());
          },
        }
      );
    },
    [onConnectionChange, getActionInfo, preventAutoLayout, setSelectedEdgeIds]
  );

  const nodesConnectable = updatedNodes.some(({connectable}) => connectable);

  return (
    <ReactFlow
      nodes={updatedNodes}
      edges={updatedEdges}
      edgeTypes={EDGE_TYPES}
      nodeTypes={NODE_TYPES}
      nodesConnectable={nodesConnectable}
      nodesDraggable={nodesConnectable}
      onKeyDown={onKeyDown}
      onMoveStart={highlightedStepIds.size > 0 ? clearHighlights : undefined}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onNodesDelete={onNodesDelete}
      {...(nodesConnectable
        ? {onConnect, onConnectStart, onConnectEnd, onEdgeUpdate, onEdgeUpdateStart, onEdgesDelete}
        : {})}
      {...defaultReactFlowProps}
      {...STATIC_PROPS}
    >
      <DefaultBackground />
      <div className={css(styles.createNodeButtonPositioningWrapper)}>
        <div className={css(styles.createNodeButton)}>
          <AddSourceStepButton />
        </div>
      </div>
      <AnalysisFlowchartToolbar />
      <HorizontalControls
        additionalControls={
          <>
            {nodesConnectable && (
              <BaseControlButton
                icon="grid-two"
                tooltip="Auto layout"
                onClick={() => {
                  setNodePositions({});
                }}
                disabled={Object.values(nodePositions).length === 0}
                dataTestAttrs={{component: 'AnalysisFlowchart', element: 'autoLayoutButton'}}
              />
            )}
          </>
        }
      />
    </ReactFlow>
  );
}

const styles = StyleSheet.create({
  createNodeButtonPositioningWrapper: {position: 'absolute', top: '12px', right: '12px'},
  // ReactFlow uses a z-index of 4 for it's flowchart, so to overlay the create button it must be >= 4
  createNodeButton: {position: 'relative', zIndex: 4},
});
